import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { Box, Typography } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'

import DashboardService, {
  getConversationLogs,
} from '@/services/DashboardService'
import DatagridUser from '@/components/UI/Users/DatagridUser'
import { LEARNER_CONVERSATIONS } from '@/config'

const dashboardService = new DashboardService()

interface ConvoLog {
  _id: string
  agent_id: string
  author_id: string
  author_email: string
  channel: string
  chat_session_id: string
  question_text: string
  reply_text: string
  class_name: string
  created_at: string
  intent_id: string
}

interface ConvoLogsProps {
  filters: any
}

const ConvoLogs = (props: ConvoLogsProps) => {
  const { t } = useTranslation('translation')
  const [loading, setLoading] = useState(false)
  const [convoLogs, setConvoLogs] = useState<ConvoLog[]>([])
  const [connectionClasses, setConnectionClasses] = useState<
    Record<string, string>
  >({})
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [paginationState, setPaginationState] = useState(null)
  const rowCountRef = React.useRef(paginationState?.total_items || 0)

  const rowCount = React.useMemo(() => {
    if (paginationState?.total_items !== undefined) {
      rowCountRef.current = paginationState.total_items
    }
    return rowCountRef.current
  }, [paginationState?.total_items])

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const response: any = await getConversationLogs({
        ...props.filters,
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      })

      // Defensive handling for various possible response shapes
      const report_data = response?.data?.report_data
      let rows: ConvoLog[] = []
      if (Array.isArray(report_data?.rows)) {
        rows = report_data?.rows as ConvoLog[]
      }

      setConvoLogs(rows)

      setPaginationState({ total_items: report_data?.pagination?.total_items })
    } catch (e) {
      setConvoLogs([])
      setPaginationState({ total_items: 0 })
    } finally {
      setLoading(false)
    }
  }

  const fetchClasses = async () => {
    try {
      const response: any = await dashboardService.getClasses()
      // Build mapping from connection_id -> class name
      // Expected shape based on sample in comments:
      // response?.data?.data?.classes: Array<{ name: string; connections: Array<{ connection_id: string }> }>
      const classes = response?.data?.data?.classes
      const mapping: Record<string, string> = {}

      if (Array.isArray(classes)) {
        for (const cls of classes) {
          const className = cls?.name
          const connections = cls?.connections
          if (className && Array.isArray(connections)) {
            for (const conn of connections) {
              const id = conn?.connection_id
              if (id) {
                mapping[id] = className
              }
            }
          }
        }
      }

      setConnectionClasses(mapping)
    } catch (e) {
      setConnectionClasses({})
    }
  }

  useEffect(() => {
    fetchClasses()
  }, [])

  useEffect(() => {
    // skip if date range fitler not set
    if (!props.filters.filter_from_date || !props.filters.filter_to_date) {
      setConvoLogs([])
      setPaginationState({ total_items: 0 })
      return
    }
    fetchLogs()
  }, [props.filters, paginationModel])

  const columns: GridColDef<ConvoLog>[] = [
    {
      headerName: t('Question'),
      field: 'question_text',
      sortable: false,
      flex: 1,
    },
    {
      headerName: t('Answer'),
      field: 'reply_text',
      sortable: false,
      renderCell: params => (
        <ReactMarkdown
          components={{
            p: ({ children }) => (
              <Typography variant="body3">{children}</Typography>
            ),
          }}
        >
          {params.value}
        </ReactMarkdown>
      ),
      flex: 1,
    },
    {
      headerName: t('Asked by'),
      field: 'author_email',
      sortable: false,
      flex: 1,
      renderCell: params => {
        const { row } = params
        return (
          <DatagridUser
            user={{
              _id: row?.author_id || row?.author_email,
              email: row?.author_email,
              // first_name: row?.first_name,
              // last_name: row?.last_name,
              // profile_image: row?.profile_image,
            }}
          />
        )
      },
    },
    {
      headerName: t('Date'),
      field: 'created_at',
      sortable: false,
      flex: 1,
      renderCell: params => {
        const { row, value } = params
        const dateText = value
          ? dayjs
              .utc(value as any)
              .local()
              .format('DD/MM/YYYY hh:mm A')
          : ''
        const userIdOrEmail = row?.author_id || row?.author_email
        const sessionId = row?.chat_session_id
        if (userIdOrEmail && sessionId) {
          const to = LEARNER_CONVERSATIONS.replace(
            ':userIdOrEmail',
            userIdOrEmail,
          ).replace(':sessionId', sessionId)
          return (
            <Link className="no-underline hover:tw-no-underline" to={to}>
              {dateText}
            </Link>
          )
        }
        return <span>{dateText}</span>
      },
    },
    {
      headerName: t('Class'),
      field: 'connection_id',
      sortable: false,
      flex: 1,
      valueFormatter: value => {
        return connectionClasses[value] ?? '-'
      },
    },
  ]

  return (
    <Box sx={{ height: 600, width: '100%', mt: 2 }}>
      <DataGrid
        getRowId={row => row._id}
        rows={convoLogs}
        columns={columns}
        loading={loading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={[5, 10, 25]}
        disableRowSelectionOnClick
        disableColumnSorting
        sx={{
          '& .MuiDataGrid-cell': {
            fontSize: '0.875rem',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#f9fafb',
            fontSize: '0.875rem',
            fontWeight: 600,
          },
        }}
      />
    </Box>
  )
}

export default ConvoLogs
